import { minutesToMilliseconds } from 'date-fns'
import StatusCodes from 'http-status-codes'
import { useCallback, useEffect } from 'react'
import useTimeoutFn from 'react-use/lib/useTimeoutFn'
import { useSnapshot } from 'valtio'

import { useAuth } from '../../context/auth'
import { useAppStatus } from '../../hooks/use-app-status'
import { getToken } from '../../lib/auth'
import { sendHeartbeat } from '../../lib/heartbeat'
import { time } from '../../util/date'

import { useUserActivity } from './use-user-activity'

const heartbeat = () =>
  getToken()
    .then(token => {
      if (!token) throw new Error('No token found.')
      return sendHeartbeat(token)
    })
    .then(response => {
      if (response.status === StatusCodes.GONE) {
        throw new Error('Session has expired.')
      }
    })

export function useIdleDetection(sessionLengthMins: number, warningBeforeLogoutMins: number) {
  const threshold = sessionLengthMins - warningBeforeLogoutMins
  const { status, setInactivityStatus } = useAppStatus()
  const { isLoggedIn } = useAuth()
  const [, , resetTimeout] = useTimeoutFn(() => {
    setInactivityStatus({ isIdle: true })
  }, minutesToMilliseconds(threshold))
  const { isIdle } = useSnapshot(status.inactivityStatus)
  const heartbeatInterval = time.ms.Minute

  const handleHeartbeatError = useCallback(() => {
    if (!isLoggedIn) return

    setInactivityStatus({ forceLogout: true })
  }, [isLoggedIn])

  const reset = useCallback(() => {
    setInactivityStatus({ isIdle: false })
    resetTimeout()
  }, [resetTimeout])

  const handleUserActivity = useCallback(() => {
    if (isIdle) return

    heartbeat()
      .then(() => reset())
      .catch(handleHeartbeatError)
  }, [handleHeartbeatError, reset, isIdle])

  useEffect(() => {
    if (!isLoggedIn || isIdle) return

    heartbeat()
      .then(() => reset())
      .catch(handleHeartbeatError)
  }, [handleHeartbeatError, reset, isIdle, isLoggedIn])

  const userInteractionHandler = useUserActivity(handleUserActivity, heartbeatInterval)

  return {
    userInteractionHandler,
    reset,
  }
}
